import type { SheetInches } from "common/types/nominalNumbersTypes";
import { sheetInchesNew } from "common/utils/nominalNumbers";
import { aabb2Copy, aabb2Scale } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3Ident, mat3Inverse, mat3Mult, mat3RotateAroundPoint, mat3Scale, mat3Translate } from "geom/mat3";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { MARGIN_INCHES } from "../plans/constants";
import type { DrawingLayout } from "../plans/layout";
import { DRAWING_LAYOUT, LayoutMethodType } from "../plans/layout";

export function drawingLayoutByAABB2WithoutMargin(
  pageWidth: number,
  pageHeight: number,
  bb: AABB2,
  contentBB: AABB2,
  staticPositionValues?: Array<number>,
): DrawingLayout {
  const dwgWidth = bb.maxX - bb.minX;
  const dwgHeight = bb.maxY - bb.minY;

  let scaleFactor;

  const pageAspectRatio = pageHeight / pageWidth;
  const dwgAspectRatio = dwgHeight / dwgWidth;

  if (dwgAspectRatio > pageAspectRatio) {
    scaleFactor = pageHeight / dwgHeight;
  } else {
    scaleFactor = pageWidth / dwgWidth;
  }

  const dwgCenterX = staticPositionValues?.[0] ?? (bb.maxX + bb.minX) / 2;
  const dwgCenterY = staticPositionValues?.[1] ?? (bb.maxY + bb.minY) / 2;

  const pageCenterX = staticPositionValues?.[2] ?? pageWidth / 2;
  const pageCenterY = staticPositionValues?.[3] ?? pageHeight / 2;

  return {
    drawingLayoutType: DRAWING_LAYOUT,
    type: LayoutMethodType.ScaleAroundCentroid,
    pageWidth,
    pageHeight,
    dwgCenterX,
    dwgCenterY,
    pageCenterX,
    pageCenterY,
    scaleFactor,
    bb: aabb2Scale(aabb2Copy(contentBB), scaleFactor),
  };
}

export function drawingLayoutByAABB2(
  width: number,
  height: number,
  bb: AABB2,
  inheritedScaleFactor: number | undefined,
  margin: number = MARGIN_INCHES,
): DrawingLayout {
  const pageInnerWidth = width - margin * 2;
  const pageInnerHeight = height - margin * 2;

  const dwgWidth = bb.maxX - bb.minX;
  const dwgHeight = bb.maxY - bb.minY;

  let scaleFactor: number;

  if (inheritedScaleFactor === undefined) {
    const pageAspectRatio = pageInnerHeight / pageInnerWidth;
    const dwgAspectRatio = dwgHeight / dwgWidth;

    if (dwgAspectRatio > pageAspectRatio) {
      scaleFactor = pageInnerHeight / dwgHeight;
    } else {
      scaleFactor = pageInnerWidth / dwgWidth;
    }
  } else {
    scaleFactor = inheritedScaleFactor;
  }

  const dwgCenterX = (bb.maxX + bb.minX) / 2;
  const dwgCenterY = (bb.maxY + bb.minY) / 2;

  const pageCenterX = width / 2;
  const pageCenterY = height / 2;

  return {
    drawingLayoutType: DRAWING_LAYOUT,
    type: LayoutMethodType.ZoomToFit,
    pageWidth: width,
    pageHeight: height,
    dwgCenterX,
    dwgCenterY,
    pageCenterX,
    pageCenterY,
    scaleFactor,
    bb: aabb2Scale(aabb2Copy(bb), scaleFactor),
  };
}

export function drawingLayoutByAABB2JustifyTop(
  width: number,
  height: number,
  bb: AABB2,
  justifyY: "left" | "right" | "center",
  inheritedScaleFactor: number | undefined,
): DrawingLayout {
  const pageInnerWidth = width;
  const pageInnerHeight = height;

  const dwgWidth = bb.maxX - bb.minX;
  const dwgHeight = bb.maxY - bb.minY;

  let scaleFactor;

  if (inheritedScaleFactor === undefined) {
    const pageAspectRatio = pageInnerHeight / pageInnerWidth;
    const dwgAspectRatio = dwgHeight / dwgWidth;

    if (dwgAspectRatio > pageAspectRatio) {
      scaleFactor = pageInnerHeight / dwgHeight;
    } else {
      scaleFactor = pageInnerWidth / dwgWidth;
    }
  } else {
    scaleFactor = inheritedScaleFactor;
  }

  const dwgCenterX = (bb.maxX + bb.minX) / 2;
  const dwgCenterY = (bb.maxY + bb.minY) / 2;

  const centerX = ((bb.maxX - bb.minX) * scaleFactor) / 2;
  let pageCenterX: number;
  let methodType: LayoutMethodType;
  switch (justifyY) {
    case "left":
      pageCenterX = centerX;
      methodType = LayoutMethodType.ZoomToFitJustifyTopLeft;
      break;
    case "right":
      pageCenterX = width - centerX;
      methodType = LayoutMethodType.ZoomToFitJustifyTopRight;
      break;
    case "center":
      pageCenterX = width / 2;
      methodType = LayoutMethodType.ZoomToFitJustifyTopCenter;
      break;
  }

  const pageCenterY = height - ((bb.maxY - bb.minY) * scaleFactor) / 2;

  return {
    drawingLayoutType: DRAWING_LAYOUT,
    type: methodType,
    pageWidth: width,
    pageHeight: height,
    dwgCenterX,
    dwgCenterY,
    pageCenterX,
    pageCenterY,
    scaleFactor,
    bb: aabb2Scale(aabb2Copy(bb), scaleFactor),
  };
}

export function ptToPaperSpace(
  x: number,
  y: number,
  { dwgCenterX: dwgcenterX, dwgCenterY: dwgcenterY, pageCenterX, pageCenterY, scaleFactor, pageHeight }: DrawingLayout,
): [SheetInches, SheetInches] {
  x -= dwgcenterX;
  y -= dwgcenterY;

  x *= scaleFactor;
  y *= scaleFactor;

  x += pageCenterX;
  y += pageCenterY;

  y *= -1;
  y += pageHeight;

  return [x as SheetInches, y as SheetInches];
}

export function xToPaperSpace(
  x: number,
  { dwgCenterX: dwgCenterX, pageCenterX, scaleFactor }: DrawingLayout,
): SheetInches {
  x -= dwgCenterX;
  x *= scaleFactor;
  x += pageCenterX;
  return x as SheetInches;
}

export function yToPaperSpace(
  y: number,
  { dwgCenterY: dwgCenterY, pageCenterY, scaleFactor, pageHeight }: DrawingLayout,
): SheetInches {
  y -= dwgCenterY;
  y *= scaleFactor;
  y += pageCenterY;
  y *= -1;
  y += pageHeight;
  return y as SheetInches;
}

export function yToModelSpace(
  y: number,
  { dwgCenterY: dwgCenterY, pageCenterY, scaleFactor, pageHeight }: DrawingLayout,
): number {
  y -= pageHeight;
  y /= -1;
  y -= pageCenterY;
  y /= scaleFactor;
  y += dwgCenterY;
  return y;
}

export function polyline2ToPaperSpace(pl: PolyLine2, layout: DrawingLayout) {
  const transform = modelSpaceToPaperSpaceTransform(layout);
  const transformed = polyLine2New(pl.length);
  for (let i = 0; i < pl.length; i += 2) {
    const [xk, yk] = mat3Dot(transform, pl[i], pl[i + 1]);
    transformed[i] = xk;
    transformed[i + 1] = yk;
  }
  return transformed;
}

export function modelSpaceToPaperSpaceTransform(layout: DrawingLayout | undefined): Mat3 {
  if (!layout) {
    return mat3Ident();
  }

  const { pageHeight, dwgCenterX, dwgCenterY, pageCenterX, pageCenterY, scaleFactor } = layout;

  let pdfMat = mat3Translate(-dwgCenterX, -dwgCenterY);
  pdfMat = mat3Mult(mat3Scale(scaleFactor, scaleFactor), pdfMat);
  pdfMat = mat3Mult(mat3Translate(pageCenterX, pageCenterY), pdfMat);
  pdfMat = mat3Mult(mat3Scale(1, -1), pdfMat);
  pdfMat = mat3Mult(mat3Translate(0, pageHeight), pdfMat);

  return pdfMat;
}

export function modelSpaceToPaperSpaceTransformWithPlanRotation(layout: DrawingLayout, planRotationRads: number): Mat3 {
  return mat3Mult(
    mat3RotateAroundPoint(layout?.pageCenterX ?? 0, layout?.pageCenterY ?? 0, planRotationRads),
    modelSpaceToPaperSpaceTransform(layout),
  );
}

export function paperSpaceToModelSpaceTransform(layout: DrawingLayout, flipY: boolean): Mat3 {
  let mat = mat3Inverse(modelSpaceToPaperSpaceTransform(layout));
  if (flipY) {
    mat = mat3Mult(mat3Scale(1, -1), mat);
  }
  return mat;
}

/**
 * @deprecated use sheetInchesToPoints
 * @TODO
 * - This is just a passthrough for now so that the
 * fontSize refactor changeset is smaller.
 *  - We should replace all usage with sheetInchesToPoints
 */
export function inches(value: number): SheetInches {
  return sheetInchesNew(value);
}
